import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useApi } from '../api/client';
import { useAuth } from '../auth/AuthProvider';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useToast } from '../components/Toast';
import type { ApiError } from '../api/client';

type MetricsSettings = {
  ratingScale: { score: number; label: string; percentage: number }[];
  bonusLevels: { key: string; label: string; factor: number }[];
};

type AssignmentDetail = {
  AssignmentId: number;
  EmployeeUpn: string;
  KpiCode: string;
  KpiTitle: string;
  Weight: number;
  TargetValue: number | null;
  HasSelfRating: number;
  HasManagerRating: number;
  WeightedContribution: number | null;
  selfRating?: { score: number | null; comment: string | null; bonusFactor?: number | null };
  managerRating?: { score: number | null; comment: string | null; bonusFactor?: number | null };
  canManagerReview: boolean;
};

const defaultBonusLevels = [
  { key: 'normal', label: 'Normal (1.0)', factor: 1.0 },
  { key: 'strong', label: 'Strong (1.25)', factor: 1.25 },
  { key: 'exceptional', label: 'Exceptional (1.5)', factor: 1.5 },
];

const factorToLevel = (factor?: number | null, bonusLevels: { key: string; factor: number }[] = defaultBonusLevels) => {
  const found = bonusLevels.find(b => Math.abs(b.factor - (factor ?? 1)) < 0.01);
  return found?.key ?? 'normal';
};

function managerScoreUnrated(score: number | null | undefined): boolean {
  if (score === null || score === undefined) return true;
  const n = Number(score);
  return !Number.isNaN(n) && n === 0;
}

export const RatingPage = () => {
  const { employeeUpn, kpiCode, periodId } = useParams();
  const decodedUpn = employeeUpn ? decodeURIComponent(employeeUpn) : undefined;
  const navigate = useNavigate();
  const api = useApi();
  const { user } = useAuth();
  const qc = useQueryClient();
  const toast = useToast();
  const isSelf = user?.upn && decodedUpn ? user.upn.toLowerCase() === decodedUpn.toLowerCase() : true;

  const detailQuery = useQuery({
    queryKey: ['assignment-detail', decodedUpn, kpiCode, periodId],
    enabled: Boolean(kpiCode && periodId),
    queryFn: async () => {
      const params = new URLSearchParams({
        kpiCode: kpiCode ?? '',
        periodId: periodId ?? '',
      });
      if (decodedUpn) params.append('employeeUpn', decodedUpn);
      return api.get(`/assignments/detail?${params.toString()}`) as Promise<AssignmentDetail>;
    }
  });

  // Fetch dynamic metrics settings (bonus levels, rating scale)
  const metricsQuery = useQuery({
    queryKey: ['metrics-settings'],
    queryFn: () => api.get('/settings/metrics') as Promise<MetricsSettings>,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });

  const bonusLevels = useMemo(() => {
    const levels = metricsQuery.data?.bonusLevels;
    if (!levels || levels.length === 0) return defaultBonusLevels;
    return levels;
  }, [metricsQuery.data?.bonusLevels]);

  const maxScore = useMemo(() => {
    const scale = metricsQuery.data?.ratingScale;
    if (!scale || scale.length === 0) return 4;
    return Math.max(...scale.map(s => s.score));
  }, [metricsQuery.data?.ratingScale]);

  const detail = detailQuery.data;

  const [selfScore, setSelfScore] = useState<number>(3);
  const [selfNotes, setSelfNotes] = useState<string>('');
  const [selfBonus, setSelfBonus] = useState<string>('normal');

  const [mgrScore, setMgrScore] = useState<number>(3);
  const [mgrNotes, setMgrNotes] = useState<string>('');
  const [mgrBonus, setMgrBonus] = useState<string>('normal');
  // Notes start as '' and are filled when detail loads. That initial empty
  // state is not an edit — posting it would COALESCE a blank over the saved comment.
  const selfNotesDirty = useRef(false);
  const mgrNotesDirty = useRef(false);
  const selfNotesBaseline = useRef('');
  const mgrNotesBaseline = useRef('');

  useEffect(() => {
    if (!detail) return;
    const selfNote = detail.selfRating?.comment ?? '';
    const mgrNote = detail.managerRating?.comment ?? '';
    setSelfScore(detail.selfRating?.score ?? 3);
    setSelfNotes(selfNote);
    selfNotesBaseline.current = selfNote;
    selfNotesDirty.current = false;
    setSelfBonus(factorToLevel(detail.selfRating?.bonusFactor, bonusLevels));
    setMgrScore(detail.managerRating?.score ?? 3);
    setMgrNotes(mgrNote);
    mgrNotesBaseline.current = mgrNote;
    mgrNotesDirty.current = false;
    setMgrBonus(factorToLevel(detail.managerRating?.bonusFactor, bonusLevels));
  }, [detail?.AssignmentId, detail?.selfRating, detail?.managerRating, bonusLevels]);

  const commentIfEdited = (dirty: boolean, notes: string, baseline: string): string | undefined => {
    if (!dirty || notes === baseline) return undefined;
    return notes;
  };

  const selfMutation = useMutation({
    mutationKey: ['rating-self', detail?.AssignmentId],
    mutationFn: () => {
      const body: { kpiCode?: string; periodId: number; score: number; bonusLevel: string; comment?: string } = {
        kpiCode,
        periodId: Number(periodId),
        score: selfScore,
        bonusLevel: selfBonus,
      };
      const comment = commentIfEdited(selfNotesDirty.current, selfNotes, selfNotesBaseline.current);
      if (comment !== undefined) body.comment = comment;
      return api.post(
        '/ratings/self',
        body,
        decodedUpn ? { 'X-Employee-Upn': decodedUpn } : undefined,
      );
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['assignment-detail', decodedUpn, kpiCode, periodId] });
      toast.success('Self rating saved successfully!');
    },
    onError: (error) => {
      const detail = (error as ApiError)?.body?.detail || 'Failed to save self rating';
      toast.error(detail);
    }
  });

  const managerMutation = useMutation({
    mutationKey: ['rating-manager', detail?.AssignmentId],
    mutationFn: () => {
      const body: { kpiCode?: string; periodId: number; score: number; bonusLevel: string; comment?: string } = {
        kpiCode,
        periodId: Number(periodId),
        score: mgrScore,
        bonusLevel: mgrBonus,
      };
      const comment = commentIfEdited(mgrNotesDirty.current, mgrNotes, mgrNotesBaseline.current);
      if (comment !== undefined) body.comment = comment;
      return api.post(
        '/ratings/review',
        body,
        {
          ...(decodedUpn ? { 'X-Employee-Upn': decodedUpn } : {}),
          ...(user?.upn ? { 'X-Manager-Upn': user.upn } : {}),
        },
      );
    },
    onSuccess: async () => {
      qc.invalidateQueries({ queryKey: ['assignment-detail', decodedUpn, kpiCode, periodId] });
      const upn = decodedUpn || detail?.EmployeeUpn;
      const pid = Number(periodId);
      if (!upn || !kpiCode || !Number.isFinite(pid)) {
        toast.success('Manager review saved successfully!');
        return;
      }
      try {
        const data = await api.get(`/assignments/self?endDate=2099-12-31&employeeUpn=${encodeURIComponent(upn)}`) as {
          items?: { KpiCode: string; PeriodId: number; ManagerScore: number | null }[];
        };
        const ordered = (data?.items || [])
          .filter(r => Number(r.PeriodId) === pid)
          .sort((a, b) => String(a.KpiCode).localeCompare(String(b.KpiCode)));
        const idx = ordered.findIndex(r => r.KpiCode === kpiCode);
        const rotated = idx >= 0 ? [...ordered.slice(idx + 1), ...ordered.slice(0, idx)] : ordered;
        const next = rotated.find(r => r.KpiCode !== kpiCode && managerScoreUnrated(r.ManagerScore));
        if (next) {
          toast.success('Manager review saved. Opening the next unrated KPI.');
          navigate(`/rating/${encodeURIComponent(upn)}/${next.KpiCode}/${pid}`);
          return;
        }
        const stillOpen = ordered.some(r => managerScoreUnrated(r.ManagerScore));
        toast.success(stillOpen
          ? 'Manager review saved successfully!'
          : 'Manager review saved. All KPIs in this period are rated.');
      } catch {
        toast.success('Manager review saved successfully!');
      }
      navigate(`/team?employee=${encodeURIComponent(upn)}`);
    },
    onError: (error) => {
      const detail = (error as ApiError)?.body?.detail || 'Failed to save manager review';
      toast.error(detail);
    }
  });

  const weight = detail?.Weight ?? 0;
  const previewContribution = useMemo(() => {
    const factor = bonusLevels.find(b => b.key === (isSelf ? selfBonus : mgrBonus))?.factor ?? 1;
    const score = isSelf ? selfScore : mgrScore;
    return Number((((score || 0) / maxScore) * weight * factor).toFixed(3));
  }, [weight, selfBonus, selfScore, mgrBonus, mgrScore, isSelf, bonusLevels, maxScore]);

  if (detailQuery.isLoading) return <p>Loading KPI...</p>;
  if (detailQuery.error || !detail) return <p>Unable to load assignment.</p>;

  return (
    <div>
      <h2>KPI Rating</h2>
      <div className="panel">
        <div style={{display:'flex', justifyContent:'space-between', gap:'1rem', flexWrap:'wrap'}}>
          <div>
            <div className="muted-label">KPI</div>
            <div style={{fontSize:'1.25rem', fontWeight:600}}>{detail.KpiTitle}</div>
          </div>
          <div>
            <div className="muted-label">Employee</div>
            <div>{detail.EmployeeUpn}</div>
          </div>
          <div>
            <div className="muted-label">Weight</div>
            <div>{detail.Weight?.toFixed(2)}</div>
          </div>
          <div>
            <div className="muted-label">Current Contribution</div>
            <div>{detail.WeightedContribution?.toFixed(3) ?? '—'}</div>
          </div>
        </div>
      </div>

      {isSelf ? (
        <section className="panel">
          <h3>Your self rating</h3>
          <RatingForm
            score={selfScore}
            notes={selfNotes}
            bonusLevel={selfBonus}
            onScoreChange={setSelfScore}
            onNotesChange={(value) => { selfNotesDirty.current = true; setSelfNotes(value); }}
            onBonusChange={setSelfBonus}
            onSubmit={() => selfMutation.mutate()}
            submitting={selfMutation.isPending}
            success={selfMutation.isSuccess}
            error={selfMutation.isError}
            preview={previewContribution}
            ctaLabel={detail.HasSelfRating ? 'Update Self Rating' : 'Submit Self Rating'}
            bonusLevels={bonusLevels}
            maxScore={maxScore}
          />
        </section>
      ) : (
        <section className="panel">
          <h3>Employee self rating</h3>
          {detail.selfRating ? (
            <>
              <p><strong>Score:</strong> {detail.selfRating.score ?? '—'}</p>
              <p><strong>Notes:</strong> {detail.selfRating.comment || '—'}</p>
            </>
          ) : (
            <p>Employee has not submitted their self rating yet.</p>
          )}
        </section>
      )}

      {!isSelf && (
        <section className="panel">
          <h3>Your manager review</h3>
          <RatingForm
            score={mgrScore}
            notes={mgrNotes}
            bonusLevel={mgrBonus}
            onScoreChange={setMgrScore}
            onNotesChange={(value) => { mgrNotesDirty.current = true; setMgrNotes(value); }}
            onBonusChange={setMgrBonus}
            onSubmit={() => managerMutation.mutate()}
            submitting={managerMutation.isPending}
            success={managerMutation.isSuccess}
            error={managerMutation.isError}
            preview={previewContribution}
            ctaLabel={detail.HasManagerRating ? 'Update Review' : 'Submit Review'}
            disabled={!detail.canManagerReview}
            bonusLevels={bonusLevels}
            maxScore={maxScore}
          />
        </section>
      )}
    </div>
  );
};

const RatingForm: React.FC<{
  score: number;
  notes: string;
  bonusLevel: string;
  onScoreChange: (value: number) => void;
  onNotesChange: (value: string) => void;
  onBonusChange: (value: string) => void;
  onSubmit: () => void;
  submitting: boolean;
  success: boolean;
  error: boolean;
  preview: number;
  ctaLabel: string;
  disabled?: boolean;
  bonusLevels: { key: string; label: string; factor: number }[];
  maxScore: number;
}> = ({ score, notes, bonusLevel, onScoreChange, onNotesChange, onBonusChange, onSubmit, submitting, success, error, preview, ctaLabel, disabled, bonusLevels, maxScore }) => (
  <form
    onSubmit={e => { e.preventDefault(); if (!disabled) onSubmit(); }}
    className="rating-form"
  >
    <label>
      Score (0-{maxScore})
      <input type="number" min={0} max={maxScore} step={0.1} value={score} onChange={e => onScoreChange(Number(e.target.value))} disabled={disabled} />
    </label>
    <label>
      Bonus level
      <select value={bonusLevel} onChange={e => onBonusChange(e.target.value)} disabled={disabled}>
        {bonusLevels.map(opt => <option key={opt.key} value={opt.key}>{opt.label}</option>)}
      </select>
    </label>
    <div className="muted-label">Weighted contribution preview: {preview.toFixed(3)}</div>
    <label>
      Notes
      <textarea value={notes} onChange={e => onNotesChange(e.target.value)} disabled={disabled} />
    </label>
    <button type="submit" disabled={submitting || disabled}>{submitting ? 'Saving...' : ctaLabel}</button>
    {success && <span style={{marginLeft:8}}>Saved</span>}
    {error && <span style={{marginLeft:8, color:'salmon'}}>Error</span>}
  </form>
);
